import { SPORT_IDS } from '@/pages/(guarded)/results/components/consts';

type Diff<A extends object, B extends A> = {
  [K in keyof (A | B) as string]?: [A[K] | undefined, B[K] | undefined];
};

export const PRODUCT_DEFAULT_DESCRIPTION = {
  addOn:
    '{calc.plays} {product.ui.subtitle||product.ui.title||product.title} + {product.ui.addOn.title||product.products[{variety.addOn.productId}].title||"add-on"}',
  combos: '{calc.plays} {product.ui.subtitle||product.ui.title||product.title}',
  addOnCombos:
    '{calc.plays} {product.ui.subtitle||product.ui.title||product.title} + {product.ui.addOn.title||product.products[{variety.addOn.productId}].title||"add-on"}',
  default: '{calc.plays} {product.ui.subtitle||product.ui.title||product.title}',
};

export const OFFER_MATRIX_MAP = {
  id: ['{product.services[{this}].name||"Service"}'], // @todo
  // ids: ['Services'], // @todo
  // auto: ['Scaling'], // @todo
  // bonus: ['With free {product.services[{this}].name||"bonus"}'],
  plays: ['{this} {product.ui.shortTitle||""} plays||"plays"'],
  // prorated: ['Prorated', 'Not Prorated'], // @todo
  // combos: undefined,
  // combos: [
  //   '{product.ui.combos.title||matrix[0].option[0].ui.title||product.products[{this[0].productId}].title||"combo"}',
  //   'No {product.ui.combos.title||matrix[0].option[0].ui.title||product.products[{matrix[0].option[0].productId}].title||"combo"}',
  //   'Do not add {product.ui.combos.title||matrix[0].option[0].ui.title||product.products[{matrix[0].option[0].productId}].title||"combo"}',
  // ],
  addOn: [
    // [0] Title when `over` but no `offer.ui.option.KEY.title` nor `over.ui.title` nor `offer.ui.title`
    '{product.ui.addOn.title||matrix[0].option.ui.title||product.products[{this.productId}].title||"add-on"}',
    // [1] Title when no `over` but no `offer.ui.option.KEY.title`
    'No {product.ui.addOn.skipTitle||matrix[0].option.ui.skipTitle||product.ui.addOn.title||matrix[0].option.ui.title||product.products[{matrix[0].option.productId}].title||"add-on"}',
    // [2] Description when no `over` but no `offer.ui.option.KEY.description`
    'Do not add {product.ui.addOn.skipTitle||matrix[0].option.ui.skipTitle||product.ui.addOn.title||matrix[0].option.ui.title||product.products[{matrix[0].option.productId}].title||"add-on"}',
  ],
  time: ['{duration}', '{subscription.interval}'],
};

// You can also do idsSubtitle for an extra text
export const OFFER_MATRIX_TITLES = {
  id: 'Single service',
  // ids: 'Multi-service',
  auto: 'With price scaling',
  bonus: 'With bonus offer',
  plays: 'Plays',
  prorated: 'With prorated pricing',
  // combos: 'Combos',
  addOn: 'Add-ons',
  duration: 'Duration',
  'time.duration': 'Duration',
  'time.subscription.interval': 'Subscription frequency',
};

export const OFFER_MATRIX_KEYS = new Set(Object.keys(OFFER_MATRIX_MAP));

export const OFFER_MATRIX_KEYS_IGNORE = new Set(
  Object.keys(OFFER_MATRIX_MAP).map((key) => `${key}.ui`),
);

export function sportIdFromName(id?: API.Id | string) {
  return SPORT_IDS[id as keyof typeof SPORT_IDS] || id;
}

export function sportNameFromId(id?: API.Id) {
  for (const key in SPORT_IDS) {
    if (SPORT_IDS[key as keyof typeof SPORT_IDS] === id) {
      return key;
    }
  }

  return undefined;
}

export function areObjectsDeeplyDifferent<A extends object, B extends A>(
  obj1: A | undefined | null,
  obj2: B | undefined | null,
  path = '',
  skip?: Set<string>,
  only?: Set<string>,
): keyof Diff<A, B> | undefined {
  if (obj1) {
    for (const key in obj1) {
      const currentPath: keyof Diff<A, B> = path ? `${path}.${key}` : key;
      const [first] = currentPath.split('.', 1);

      if (skip?.has(currentPath)) {
        continue;
      }

      if (only && !only.has(first)) {
        continue;
      }

      if (obj1?.hasOwnProperty(key)) {
        if (!obj2?.hasOwnProperty(key)) {
          return currentPath;
        } else if (typeof obj1[key] === 'object' && typeof obj2?.[key] === 'object') {
          const nestedDiff = areObjectsDeeplyDifferent(
            obj1[key] as A,
            obj2[key] as B,
            currentPath,
            skip,
            only,
          );

          if (nestedDiff !== undefined) {
            return nestedDiff;
          }
        } else if (obj1[key] !== obj2?.[key]) {
          return currentPath;
        }
      }
    }
  }

  if (obj2) {
    for (const key in obj2) {
      const currentPath: keyof Diff<A, B> = path ? `${path}.${key}` : key;
      const [first] = currentPath.split('.', 1);

      if (skip?.has(currentPath)) {
        continue;
      }

      if (only && !only.has(first)) {
        continue;
      }

      if (obj2.hasOwnProperty(key) && !obj1?.hasOwnProperty(key)) {
        return currentPath;
      }
    }
  }

  return undefined;
}

export function deepCompareObjects<A extends object, B extends A>(
  obj1: A,
  obj2: B,
  path = '',
  skip?: Set<string>,
  only?: Set<string>,
) {
  const diff: Diff<A, B> = {};

  for (const key in obj1) {
    const currentPath: keyof Diff<A, B> = path ? `${path}.${key}` : key;
    const [first] = currentPath.split('.', 1);

    if (skip?.has(currentPath)) {
      continue;
    }

    if (only && !only.has(first)) {
      continue;
    }

    if (obj1?.hasOwnProperty(key)) {
      if (!obj2?.hasOwnProperty(key)) {
        diff[currentPath] = [obj1[key], undefined] as Diff<A, B>[keyof Diff<A, B>];
      } else if (
        typeof obj1[key] === 'object' &&
        typeof obj2?.[key] === 'object' &&
        // If they are both arrays, skip, because we don't want to generate an option for each key
        !(Array.isArray(obj1[key]) && Array.isArray(obj2[key]))
      ) {
        const nestedDiff = deepCompareObjects(
          obj1[key] as A,
          obj2[key] as B,
          currentPath,
          skip,
          only,
        );

        if (Object.keys(nestedDiff).length > 0) {
          Object.assign(diff, nestedDiff);
        }
      } else if (obj1[key] !== obj2?.[key]) {
        diff[currentPath] = [obj1[key], obj2?.[key]] as Diff<A, B>[keyof Diff<A, B>];
      }
    }
  }

  if (obj2) {
    for (const key in obj2) {
      const currentPath: keyof Diff<A, B> = path ? `${path}.${key}` : key;
      const [first] = currentPath.split('.', 1);

      if (skip?.has(currentPath)) {
        continue;
      }

      if (only && !only.has(first)) {
        continue;
      }

      if (obj2.hasOwnProperty(key) && !obj1?.hasOwnProperty(key)) {
        diff[currentPath] = [undefined, obj2[key]] as Diff<A, B>[keyof Diff<A, B>];
      }
    }
  }

  return diff;
}

export function matrixFromOffers<T extends API.MembershipProduct<true>>(product?: T) {
  if (!product) {
    return undefined;
  }

  const { offers } = product;

  const diffs = new Set<string>();
  const knownValues = new Map<string, Set<T[keyof T]>>();
  const map = new Map<string, Map<string, { option: T[keyof T]; indexes: Set<number> }>>();
  // const map = new Map<T[keyof T], number>();

  for (const offer1idx in offers) {
    const offer1 = offers[offer1idx];

    const skip = new Set<string>();

    for (const offer2 of offers) {
      if (offer2 === offer1) {
        continue;
      }

      const comparison = deepCompareObjects(
        offer1,
        offer2,
        '',
        OFFER_MATRIX_KEYS_IGNORE,
        OFFER_MATRIX_KEYS,
      );

      for (const key in comparison) {
        const x = comparison[key as keyof typeof comparison];
        const value1 = x?.[0] as T[keyof T];

        skip.add(key);
        diffs.add(key);

        const prevKnown = knownValues.get(key) || new Set<T[keyof T]>();

        if (!prevKnown.has(value1)) {
          prevKnown.add(value1);
          knownValues.set(key, prevKnown);
        } else {
          // console.log('SKIPPING PREV KNOWN', key, prevKnown, value1);
          // continue;
        }

        const prev =
          map.get(key) || new Map<string, { option: T[keyof T]; indexes: Set<number> }>();

        const json = `${JSON.stringify(value1)}`;
        const prevIndexes = prev.get(json)?.indexes || new Set<number>();

        prev.set(json, { option: value1, indexes: prevIndexes.add(+offer1idx) });
        map.set(key, prev);
      }
    }
  }

  return map;
}

export function getFrequencyLabels(interval: string) {
  let [timeDuration, timeUnit] = interval.split(' ');
  timeUnit = timeUnit?.replace(/s$/, ''); // Remove trailing 's' if it exists

  let label;
  if (timeDuration === '1' && timeUnit === 'year') {
    label = 'Annually';
  } else if (timeDuration === '1' && timeUnit === 'month') {
    label = 'Monthly';
  } else {
    label = `every ${timeDuration} ${timeUnit}`;
  }
  return {
    value: interval,
    label,
    priceSuffix: `/${timeUnit}`,
  };
}
